import { ElMessage, type MessageParams, type MessageHandler } from "element-plus";

/**
 * 消息管理器配置接口
 */
export interface MessageConfig {
  /** 最大同时显示数量 */
  max: number;
  /** 相同内容分组 */
  grouping: boolean;
  /** 默认显示时间（毫秒） */
  defaultDuration: number;
  /** 显示关闭按钮 */
  showClose: boolean;
  /** 防抖时间（毫秒） */
  debounceTime: number;
}

class MessageManager {
  // 消息队列：存储当前正在显示的消息内容
  private messageQueue: Array<{ id: number; content: string; handler: MessageHandler }> = [];
  // 消息ID计数器
  private idCounter = 0;

  // 配置项
  private config = {
    max: 3, // 最大同时显示数量
    grouping: true, // 相同内容分组
    defaultDuration: 0, // 默认显示时间（毫秒）
    showClose: true, // 显示关闭按钮
    debounceTime: 500, // 防抖时间（毫秒）
  };

  // 防抖记录：防止短时间内重复触发
  private debounceMap = new Map<string, number>();

  /**
   * 更新配置
   */
  public setConfig(config: Partial<typeof this.config>): void {
    Object.assign(this.config, config);
  }

  /**
   * 检查是否可以显示消息
   */
  private canShowMessage(content: string): boolean {
    const now = Date.now();

    // 防抖检查：相同内容在指定时间内不重复显示
    if (this.debounceMap.has(content)) {
      const lastTime = this.debounceMap.get(content)!;
      if (now - lastTime < this.config.debounceTime) {
        return false;
      }
    }

    // 分组检查：如果已存在相同内容的消息，并且分组开启
    if (this.config.grouping) {
      const exists = this.messageQueue.some((item) => item.content === content);
      if (exists) {
        return false;
      }
    }

    // 数量限制检查
    if (this.messageQueue.length >= this.config.max) {
      // 移除最早的一条消息
      this.removeOldestMessage();
    }

    return true;
  }

  /**
   * 移除最早的消息
   */
  private removeOldestMessage(): void {
    if (this.messageQueue.length > 0) {
      const oldest = this.messageQueue[0];
      oldest?.handler.close();
      this.messageQueue.shift();
    }
  }

  /**
   * 添加消息到队列
   */
  private addToQueue(content: string, handler: MessageHandler): number {
    const id = ++this.idCounter;
    this.messageQueue.push({ id, content, handler });
    this.debounceMap.set(content, Date.now());
    return id;
  }

  /**
   * 从队列中移除消息
   */
  private removeFromQueue(id: number): void {
    const index = this.messageQueue.findIndex((item) => item.id === id);
    if (index > -1) {
      this.messageQueue.splice(index, 1);
    }

    // 清理过期的防抖记录（5分钟后清理）
    setTimeout(() => {
      this.debounceMap.forEach((timestamp, content) => {
        if (Date.now() - timestamp > 300000) {
          // 5分钟
          this.debounceMap.delete(content);
        }
      });
    }, 60000);
  }

  /**
   * 创建消息实例
   */
  private createMessage(
    type: "success" | "error" | "warning" | "info",
    content: string,
    options?: MessageParams
  ): MessageHandler | null {
    // 检查是否可以显示
    if (!this.canShowMessage(content)) {
      return null;
    }

    // 合并选项
    const messageOptions: MessageParams = {
      message: content,
      duration: this.config.defaultDuration,
      showClose: this.config.showClose,
      onClose: () => {
        this.removeFromQueue(messageId);
        if (
          typeof options === "object" &&
          options !== null &&
          "onClose" in options &&
          typeof options.onClose === "function"
        ) {
          options.onClose();
        }
      },
      ...(options as Record<string, any>),
    };

    // 创建消息
    let handler: MessageHandler;
    switch (type) {
      case "success":
        handler = ElMessage.success(messageOptions);
        break;
      case "error":
        handler = ElMessage.error(messageOptions);
        break;
      case "warning":
        handler = ElMessage.warning(messageOptions);
        break;
      case "info":
        handler = ElMessage.info(messageOptions);
        break;
      default:
        handler = ElMessage(messageOptions);
    }

    // 添加到队列
    const messageId = this.addToQueue(content, handler);

    return handler;
  }

  /**
   * 公共方法
   */
  public success = (content: string, options?: MessageParams): MessageHandler | null => {
    return this.createMessage("success", content, options);
  };

  public error = (content: string, options?: MessageParams): MessageHandler | null => {
    return this.createMessage("error", content, options);
  };

  public warning = (content: string, options?: MessageParams): MessageHandler | null => {
    return this.createMessage("warning", content, options);
  };

  public info = (content: string, options?: MessageParams): MessageHandler | null => {
    return this.createMessage("info", content, options);
  };

  /**
   * 清空所有消息
   */
  public clearAll(): void {
    // 关闭所有消息
    this.messageQueue.forEach((item) => {
      item.handler.close();
    });

    // 清空队列
    this.messageQueue = [];
    this.debounceMap.clear();
  }

  /**
   * 获取当前队列状态（调试用）
   */
  public getQueueState() {
    return {
      count: this.messageQueue.length,
      messages: this.messageQueue.map((item) => item.content),
      config: { ...this.config },
    };
  }
}

// 创建单例实例
export const messageManager = new MessageManager();

// 导出简化的使用方法
export const message = {
  success: (content: string, options?: MessageParams) => messageManager.success(content, options),
  error: (content: string, options?: MessageParams) => messageManager.error(content, options),
  warning: (content: string, options?: MessageParams) => messageManager.warning(content, options),
  info: (content: string, options?: MessageParams) => messageManager.info(content, options),
  clearAll: () => messageManager.clearAll(),
  setConfig: (config: Partial<(typeof messageManager)["config"]>) => messageManager.setConfig(config),
  getQueueState: () => messageManager.getQueueState(),
};

// 默认导出
export default message;
